import { createLogger } from "@/logger";
import { type BootstrapEventMetadata, BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import { recordEvent } from "@/services/telemetry";
import { indexedDbAvailable } from "@/store/indexedDbAvailable";
import { deleteDatabaseBounded } from "@/store/persistence/idbConnection";
import { snapshotScope, version, workspaceId } from "@/store/scope";
import { noteRestoreFailure, restoreSnapshot, startSnapshotPersistence } from "@/store/snapshot";

const log = createLogger("storage");

const LEGACY_PGLITE_PREFIX = "/pglite/";
const REGISTRY_SUFFIX = "sbweb-bootstrap-registry";
const SNAPSHOT_PREFIX = "sbweb-store-";

/** What the one-time legacy sweep managed to remove, and what another tab is still holding. */
export type LegacyCleanupResult = { deleted: number; blocked: string[] };

/**
 * Deletes local databases from earlier data eras: every PGlite data dir (pre-5.0), plus
 * registries and snapshots whose scope doesn't match the current version. This is the one-time
 * cold-boot migration — after it, the only local data is this version's snapshot + registry.
 *
 * Deletes are bounded and their outcome is reported. A database another tab still holds open stays
 * queued for deletion in the browser, and every later open on that name queues behind it, so a
 * blocked name is the difference between "migrated" and "this tab is about to hang" — boot needs
 * to know which happened rather than reading a count that cannot tell them apart.
 *
 * @returns How many databases were actually removed, and the names still blocked by another tab.
 */
export async function cleanupLegacyDatabases(): Promise<LegacyCleanupResult> {
  if (!indexedDbAvailable() || !indexedDB.databases) return { deleted: 0, blocked: [] };
  try {
    const databases = await indexedDB.databases();
    const stale = (databases ?? [])
      .map((db) => db.name)
      .filter((name): name is string => {
        if (!name) return false;
        if (name.includes("--remove-db---")) return true;
        if (name.startsWith(LEGACY_PGLITE_PREFIX)) return true;
        if (name.endsWith(REGISTRY_SUFFIX) && !name.startsWith(`${version}-`)) return true;
        if (name.startsWith(SNAPSHOT_PREFIX) && !name.startsWith(`${SNAPSHOT_PREFIX}${version}-`)) return true;
        return false;
      });
    // Concurrently: each blocked name waits out the same window, and serially that stacks into
    // minutes of splash before the user is told anything.
    const outcomes = await Promise.all(
      stale.map(async (name) => ({ name, outcome: await deleteDatabaseBounded(name) })),
    );
    const blocked = outcomes.filter(({ outcome }) => outcome === "blocked").map(({ name }) => name);
    const deleted = outcomes.filter(({ outcome }) => outcome === "deleted").length;
    if (deleted > 0) {
      log.info("[storeBoot] Deleted stale local databases", { count: deleted });
    }
    if (blocked.length > 0) {
      log.warn("[storeBoot] Stale databases still held by another tab", { count: blocked.length });
    }
    return { deleted, blocked };
  } catch (error) {
    log.warn("[storeBoot] Legacy database cleanup failed", { error });
    return { deleted: 0, blocked: [] };
  }
}

export type StoreBootMode = "warm" | "cold";

/**
 * How the local store came up. `blockedByOtherTabs` means a previous era's database is still held
 * open by another tab, so the migration did not happen and reads on those names would queue behind
 * the pending delete — the caller shows that state instead of booting into a spinner.
 */
export type StoreBootResult = { mode: StoreBootMode; blockedByOtherTabs: boolean };

/**
 * Nuclear cleanup for the StorageError screen: deletes every IndexedDB database plus local/session
 * storage (and optionally cookies) so the next load starts from nothing.
 */
export async function clearAllSiteData({ clearCookies = false }: { clearCookies?: boolean } = {}): Promise<void> {
  try {
    if (indexedDbAvailable() && indexedDB.databases) {
      const databases = await indexedDB.databases();
      for (const database of databases ?? []) {
        if (!database.name) continue;
        await deleteDatabaseBounded(database.name);
      }
    }
  } catch (error) {
    log.warn("[storeBoot] Failed to delete IndexedDB databases", { error });
  }
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // storage unavailable
  }
  if (clearCookies) {
    try {
      for (const cookie of document.cookie.split(";")) {
        const name = cookie.split("=")[0]?.trim();
        if (name) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    } catch {
      // cookies unavailable
    }
  }
}

/**
 * Brings the local store up before React mounts: sweeps legacy PGlite/registry databases, restores
 * the newest snapshot for this user+workspace, reconciles the bootstrap registry to the snapshot's
 * cursor timeline (or clears it so the boot runs a full bootstrap), and starts idle-time snapshot
 * persistence. Persistence is best-effort — with IndexedDB unavailable the app still runs, it just
 * cold-bootstraps every load.
 *
 * @returns Whether the store came up warm or cold, and whether another tab is holding a
 *   previous-era database open — the one condition the caller cannot boot through.
 */
export async function initializeLocalStore(): Promise<StoreBootResult> {
  const cleanup = await cleanupLegacyDatabases();
  const blockedByOtherTabs = cleanup.blocked.length > 0;
  if (blockedByOtherTabs) {
    recordEvent("store.boot.blocked_by_other_tabs", { boot: { databases: cleanup.blocked.length } }, "reliability");
  }
  if (!workspaceId) return { mode: "cold", blockedByOtherTabs };

  const registry = BootstrapEventsRegistry.getInstance();
  let mode: StoreBootMode = "cold";
  try {
    const result = await restoreSnapshot(snapshotScope);
    if (result.status === "restored" && result.appMeta != null) {
      await registry.restoreWorkspace(workspaceId, result.appMeta as Record<string, BootstrapEventMetadata>);
      mode = "warm";
      log.info("[storeBoot] Snapshot restored", { rows: result.rowCount, savedAt: result.savedAt });
    } else {
      // No usable snapshot: a registry claiming this workspace is synced would make the boot run
      // an incremental bootstrap over an empty store. Clear it so the boot goes full.
      await registry.clearWorkspace(workspaceId);
      log.info("[storeBoot] No snapshot; cleared registry for full bootstrap", { status: result.status });
    }
  } catch (error) {
    log.warn("[storeBoot] Snapshot restore failed; falling back to full bootstrap", { error });
    noteRestoreFailure(error);
    recordEvent(
      "store.boot.restore_failed",
      { snapshot: { error: error instanceof Error ? error.message : String(error) } },
      "reliability",
      { isError: true },
    );
    try {
      await registry.clearWorkspace(workspaceId);
    } catch {
      // Registry unavailable degrades to a full bootstrap on its own.
    }
  }

  startSnapshotPersistence(snapshotScope, () => registry.getCachedWorkspaceEvents(workspaceId));
  return { mode, blockedByOtherTabs };
}
